import { useState } from "react";

import { useWeather, beaufortFromKmh } from "../lib/weather";
import type { ForecastHour, WeatherStatus } from "../lib/weather";

// ── Styling per status ──────────────────────────────────────────────────────
// Ring: box-shadow overlay bovenop de card-hairline — box-model ongewijzigd.
// Tokens komen uit tailwind.config.ts (warn=#FFB020, accent=#FF6B1F, risk=#FF4D6A).

const STATUS_RING: Record<WeatherStatus, string> = {
  ok: "",
  geel: "ring-1 ring-warn/40",
  oranje: "ring-1 ring-accent/40",
  rood: "ring-1 ring-risk/50",
  onbekend: "",
};

const STATUS_DOT: Record<WeatherStatus, string> = {
  ok: "bg-ok",
  geel: "bg-warn",
  oranje: "bg-accent",
  rood: "bg-risk",
  onbekend: "bg-zinc-600",
};

const STATUS_LABEL: Record<WeatherStatus, string> = {
  ok: "normaal",
  geel: "let op",
  oranje: "verhoogd",
  rood: "kritiek",
  onbekend: "onbekend",
};

const BF_COLOR: Record<number, string> = {};
function bfColor(bf: number): string {
  if (bf >= 7) return "text-risk";
  if (bf >= 6) return "text-accent";
  if (bf >= 5) return "text-warn";
  return "text-zinc-400";
}

// Initialiseer BF_COLOR lookup (0–12)
for (let i = 0; i <= 12; i++) BF_COLOR[i] = bfColor(i);

// ── Component ───────────────────────────────────────────────────────────────

/**
 * Compacte weertegel voor de KpiStrip.
 * Pollt het weer elke 15 minuten via open-meteo (KNMI seamless).
 * Klik toont 6-uurs windstoten-voorspelling + protocol-notitie.
 */
export function WeatherTile() {
  const state = useWeather();
  const [expanded, setExpanded] = useState(false);

  // Laadstatus vóór eerste succesvolle fetch
  if (!state) {
    return (
      <div className="card p-3.5">
        <div className="flex items-center gap-2 mb-2">
          <span className="w-1 h-1 rounded-full bg-zinc-700 animate-pulseSoft" />
          <span className="eyebrow">Weer</span>
        </div>
        <div className="text-2xs text-zinc-600">Ophalen...</div>
      </div>
    );
  }

  const { current, forecast6h, status, hitte, laatstOpgehaald } = state;
  const ring = STATUS_RING[status];
  const dot = STATUS_DOT[status];
  const gustsBf = beaufortFromKmh(current.windGustsKmh);

  const pad2 = (n: number) => String(n).padStart(2, "0");
  const tijdLabel = `${pad2(laatstOpgehaald.getHours())}:${pad2(
    laatstOpgehaald.getMinutes(),
  )}`;

  return (
    <div
      className={`card p-3.5 cursor-pointer select-none transition-shadow duration-200 ${ring}`}
      onClick={() => setExpanded((v) => !v)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") setExpanded((v) => !v);
      }}
      aria-expanded={expanded}
      aria-label={`Weermonitor — status ${STATUS_LABEL[status]}, klik voor details`}
    >
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-1 h-1 rounded-full ${dot}`} />
          <span className="eyebrow">Weer</span>
        </div>
        {hitte && (
          <span className="pill bg-risk/15 text-risk">
            <span className="w-1.5 h-1.5 rounded-full bg-risk animate-pulseSoft" />
            Hitte
          </span>
        )}
      </div>

      {/* ── Temperatuur (hoofdwaarde) ── */}
      <div className="text-3xl tabular font-semibold leading-none tracking-tight text-zinc-100">
        {Math.round(current.tempC)}°
      </div>

      {/* ── Windstoten ── */}
      <div
        className={`text-sm font-medium mt-1.5 tabular ${BF_COLOR[gustsBf]}`}
      >
        Stoten {Math.round(current.windGustsKmh)} km/u · Bf {gustsBf}
      </div>

      {/* ── Status + tijdstip ── */}
      <div className="text-2xs text-zinc-500 mt-2 flex items-center gap-1.5 tabular">
        <span className={`w-1 h-1 rounded-full ${dot} opacity-70`} />
        <span>{STATUS_LABEL[status]}</span>
        <span className="text-zinc-700">·</span>
        <span>{tijdLabel}</span>
      </div>

      {/* ── Uitklap-paneel: 6h-voorspelling ── */}
      {expanded && (
        <div className="mt-3 pt-3 border-t border-white/[0.06]">
          <Forecast6h forecast={forecast6h} />
          <p className="text-2xs text-zinc-500 mt-3 leading-relaxed">
            Kort onweer: muziek stoppen, schuilen — zie briefing
          </p>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function Forecast6h({ forecast }: { forecast: ForecastHour[] }) {
  if (forecast.length === 0) {
    return (
      <div className="text-2xs text-zinc-600">
        Geen voorspellingsdata beschikbaar
      </div>
    );
  }

  return (
    <div>
      <div className="eyebrow mb-2">6-uurs voorspelling</div>
      <div className="space-y-1.5">
        {forecast.map((h) => (
          <ForecastRow key={h.time} hour={h} />
        ))}
      </div>
    </div>
  );
}

function ForecastRow({ hour }: { hour: ForecastHour }) {
  const color = BF_COLOR[hour.beaufort] ?? "text-zinc-400";

  return (
    <div className="flex items-center gap-3 text-2xs tabular">
      <span className="text-zinc-600 w-10 shrink-0">{hour.time}</span>
      <span className={`font-medium ${color} w-20 shrink-0`}>
        {Math.round(hour.windGustsKmh)} km/u
      </span>
      <span className="text-zinc-600 shrink-0">Bf {hour.beaufort}</span>
      {hour.precipitationProb > 0 && (
        <span className="text-zinc-500">{hour.precipitationProb}% neerslag</span>
      )}
    </div>
  );
}
